import { ArchiveError } from '../app/errors';

/**
 * **STORE-only zip — the container half of INV-12.**
 *
 * Design §11.7 makes the encoder a non-variable: *"STORE only — no DEFLATE anywhere. deflate output
 * is not stable across zlib/library versions or between WebKit's `CompressionStream` and the Node
 * test runner. Audio is already compressed, so store-only costs almost nothing. This removes the
 * encoder as a variable entirely."* This module can therefore be about a hundred lines of field
 * layout with no encoder in it at all, and every byte it emits is a function of the entry list.
 *
 * The four container controls, each of which is a default somewhere else:
 *
 * | Control | The default it replaces |
 * |---|---|
 * | entries sorted byte-wise by name | insertion order — so the order blobs happened to be read in would reach the bytes |
 * | fixed DOS-epoch mtime on every entry | the wall clock, which changes every build and defeats every other control at once |
 * | UTF-8 flag pinned on, extra fields and comments always empty | libraries set bit 11 conditionally on whether a name is ASCII, and add extra fields (timestamps, unix attributes) at their own discretion |
 * | method 0, `compressedSize === uncompressedSize` | DEFLATE at whatever level and window the ambient library picked |
 *
 * **No zip64.** The 32-bit fields cap an archive at 4 GiB and 65,535 entries, which is roughly two
 * orders of magnitude above a season (§11.6 sizes a 40-match season at ≈120 MB stored). Rather than
 * carry a zip64 implementation nothing will exercise, `writeZip` **refuses** to emit a truncated
 * container: the limits are checked and throw an `ArchiveError` naming the entry. A silently wrong
 * archive is the one outcome BAK-1 cannot have.
 *
 * The reader is here rather than in a test helper for two reasons: the determinism tests cannot
 * verify a control they cannot parse (a test that re-implemented the layout would assert its own
 * copy), and BAK-2's import needs exactly this parser. It is as strict as the writer — anything that
 * is not a STORE-only, non-zip64 container is rejected rather than best-effort read.
 */

// ── Field constants. Wire-format values, so they live with the format. ───────

const LOCAL_HEADER_SIGNATURE = 0x0403_4b50;
const CENTRAL_HEADER_SIGNATURE = 0x0201_4b50;
const EOCD_SIGNATURE = 0x0605_4b50;

const LOCAL_HEADER_BYTES = 30;
const CENTRAL_HEADER_BYTES = 46;
const EOCD_BYTES = 22;

/** 2.0 — the floor for a container using directories. Fixed so it is never derived from content. */
const VERSION_NEEDED = 20;
const VERSION_MADE_BY = 20;

/** Bit 11 — "filename and comment are UTF-8". Pinned on so the flag never varies with the name. */
const FLAG_UTF8 = 0x0800;

/** 0 = STORE. The only method this module writes, and the only one it will read. */
const METHOD_STORE = 0;

/**
 * The **zero of the DOS date format**: 1980-01-01T00:00:00, encoded as `(year-1980)<<9 | month<<5 | day`.
 *
 * §11.7 calls for "fixed mtime 0". The literal `0` in the date field decodes to month 0 / day 0,
 * which some extractors reject as malformed; the format's own epoch is the same fixed, clock-free
 * value in a form every extractor accepts. What matters is that no wall clock reaches the bytes.
 */
const FIXED_DOS_TIME = 0x0000;
const FIXED_DOS_DATE = 0x0021;

const MAX_UINT32 = 0xffff_ffff;
const MAX_UINT16 = 0xffff;

// ── CRC-32 (IEEE 802.3), the one computation the format requires ─────────────

const CRC_TABLE = ((): Uint32Array => {
  const table = new Uint32Array(256);
  for (let n = 0; n < 256; n += 1) {
    let c = n;
    for (let k = 0; k < 8; k += 1) c = c & 1 ? 0xedb8_8320 ^ (c >>> 1) : c >>> 1;
    table[n] = c >>> 0;
  }
  return table;
})();

export function crc32(bytes: Uint8Array): number {
  let crc = 0xffff_ffff;
  for (let i = 0; i < bytes.length; i += 1) {
    crc = (CRC_TABLE[(crc ^ (bytes[i] as number)) & 0xff] as number) ^ (crc >>> 8);
  }
  return (crc ^ 0xffff_ffff) >>> 0;
}

// ── Writing ──────────────────────────────────────────────────────────────────

export interface ZipEntry {
  /** Path inside the archive, `/`-separated. */
  readonly name: string;
  readonly data: Uint8Array;
}

const encoder = new TextEncoder();

/** UTF-8 byte order, not UTF-16 code-unit order — §11.7 says byte-wise and means it. */
function compareBytes(a: Uint8Array, b: Uint8Array): number {
  const shared = Math.min(a.length, b.length);
  for (let i = 0; i < shared; i += 1) {
    const diff = (a[i] as number) - (b[i] as number);
    if (diff !== 0) return diff;
  }
  return a.length - b.length;
}

interface PreparedEntry {
  readonly name: string;
  readonly nameBytes: Uint8Array;
  readonly data: Uint8Array;
  readonly crc: number;
}

/**
 * Builds the container. Pure: the same entry list always produces the same bytes.
 *
 * @throws {ArchiveError} on a duplicate name, an empty name, or any 32-bit/16-bit field overflow.
 */
export function writeZip(entries: readonly ZipEntry[]): Uint8Array {
  if (entries.length > MAX_UINT16) {
    throw new ArchiveError(
      `An archive cannot hold more than ${MAX_UINT16} files without zip64, and this one has ${entries.length}.`,
      { context: { entries: entries.length } },
    );
  }

  const prepared: PreparedEntry[] = entries.map((entry) => {
    if (entry.name.length === 0) {
      throw new ArchiveError('An archive entry has an empty name.', {});
    }
    if (entry.data.length > MAX_UINT32) {
      throw new ArchiveError(
        `"${entry.name}" is larger than the 4 GiB a non-zip64 archive can hold.`,
        { context: { name: entry.name, bytes: entry.data.length } },
      );
    }
    return {
      name: entry.name,
      nameBytes: encoder.encode(entry.name),
      data: entry.data,
      crc: crc32(entry.data),
    };
  });

  prepared.sort((a, b) => compareBytes(a.nameBytes, b.nameBytes));

  for (let i = 1; i < prepared.length; i += 1) {
    if ((prepared[i] as PreparedEntry).name === (prepared[i - 1] as PreparedEntry).name) {
      throw new ArchiveError(`Two archive entries are both named "${(prepared[i] as PreparedEntry).name}".`, {
        context: { name: (prepared[i] as PreparedEntry).name },
      });
    }
  }

  let localSize = 0;
  let centralSize = 0;
  for (const entry of prepared) {
    localSize += LOCAL_HEADER_BYTES + entry.nameBytes.length + entry.data.length;
    centralSize += CENTRAL_HEADER_BYTES + entry.nameBytes.length;
  }
  const total = localSize + centralSize + EOCD_BYTES;
  if (total > MAX_UINT32) {
    throw new ArchiveError(
      'The archive is larger than the 4 GiB a non-zip64 container can address.',
      { context: { bytes: total } },
    );
  }

  const buffer = new ArrayBuffer(total);
  const view = new DataView(buffer);
  const out = new Uint8Array(buffer);
  const offsets: number[] = [];
  let at = 0;

  for (const entry of prepared) {
    offsets.push(at);
    view.setUint32(at, LOCAL_HEADER_SIGNATURE, true);
    view.setUint16(at + 4, VERSION_NEEDED, true);
    view.setUint16(at + 6, FLAG_UTF8, true);
    view.setUint16(at + 8, METHOD_STORE, true);
    view.setUint16(at + 10, FIXED_DOS_TIME, true);
    view.setUint16(at + 12, FIXED_DOS_DATE, true);
    view.setUint32(at + 14, entry.crc, true);
    view.setUint32(at + 18, entry.data.length, true);
    view.setUint32(at + 22, entry.data.length, true);
    view.setUint16(at + 26, entry.nameBytes.length, true);
    view.setUint16(at + 28, 0, true);
    at += LOCAL_HEADER_BYTES;
    out.set(entry.nameBytes, at);
    at += entry.nameBytes.length;
    out.set(entry.data, at);
    at += entry.data.length;
  }

  const centralStart = at;
  prepared.forEach((entry, index) => {
    view.setUint32(at, CENTRAL_HEADER_SIGNATURE, true);
    view.setUint16(at + 4, VERSION_MADE_BY, true);
    view.setUint16(at + 6, VERSION_NEEDED, true);
    view.setUint16(at + 8, FLAG_UTF8, true);
    view.setUint16(at + 10, METHOD_STORE, true);
    view.setUint16(at + 12, FIXED_DOS_TIME, true);
    view.setUint16(at + 14, FIXED_DOS_DATE, true);
    view.setUint32(at + 16, entry.crc, true);
    view.setUint32(at + 20, entry.data.length, true);
    view.setUint32(at + 24, entry.data.length, true);
    view.setUint16(at + 28, entry.nameBytes.length, true);
    view.setUint16(at + 30, 0, true); // extra field length
    view.setUint16(at + 32, 0, true); // comment length
    view.setUint16(at + 34, 0, true); // disk number start
    view.setUint16(at + 36, 0, true); // internal attributes
    view.setUint32(at + 38, 0, true); // external attributes
    view.setUint32(at + 42, offsets[index] as number, true);
    at += CENTRAL_HEADER_BYTES;
    out.set(entry.nameBytes, at);
    at += entry.nameBytes.length;
  });

  view.setUint32(at, EOCD_SIGNATURE, true);
  view.setUint16(at + 4, 0, true);
  view.setUint16(at + 6, 0, true);
  view.setUint16(at + 8, prepared.length, true);
  view.setUint16(at + 10, prepared.length, true);
  view.setUint32(at + 12, at - centralStart, true);
  view.setUint32(at + 16, centralStart, true);
  view.setUint16(at + 20, 0, true);

  return out;
}

// ── Reading ──────────────────────────────────────────────────────────────────

/** One entry as it was found on the wire — the header fields included, so a test can assert them. */
export interface ZipEntryRecord {
  readonly name: string;
  readonly data: Uint8Array;
  readonly crc32: number;
  readonly method: number;
  readonly flags: number;
  readonly dosTime: number;
  readonly dosDate: number;
}

const decoder = new TextDecoder('utf-8', { fatal: false });

/**
 * Parses a STORE-only container, verifying every CRC.
 *
 * Strict on purpose. BAK-2 imports a file the user supplies, and "best effort" on a container that
 * is not what this writer produces means importing rows out of something nobody verified.
 *
 * @throws {ArchiveError} if the container is truncated, uses any compression, carries a data
 *   descriptor or zip64 record, or fails a CRC check.
 */
export function readZip(bytes: Uint8Array): ZipEntryRecord[] {
  if (bytes.length < EOCD_BYTES) {
    throw new ArchiveError('This file is too short to be a zip archive.', {
      context: { bytes: bytes.length },
    });
  }
  const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);

  // The EOCD is last, but a trailing comment may follow it, so scan backwards for its signature.
  let eocd = -1;
  for (let i = bytes.length - EOCD_BYTES; i >= 0; i -= 1) {
    if (view.getUint32(i, true) === EOCD_SIGNATURE) {
      eocd = i;
      break;
    }
  }
  if (eocd === -1) {
    throw new ArchiveError('This file has no zip end-of-central-directory record.', {});
  }

  const count = view.getUint16(eocd + 10, true);
  const centralStart = view.getUint32(eocd + 16, true);
  if (centralStart > bytes.length) {
    throw new ArchiveError('The archive’s central directory points outside the file.', {
      context: { centralStart, bytes: bytes.length },
    });
  }

  const records: ZipEntryRecord[] = [];
  let at = centralStart;

  for (let i = 0; i < count; i += 1) {
    if (at + CENTRAL_HEADER_BYTES > bytes.length || view.getUint32(at, true) !== CENTRAL_HEADER_SIGNATURE) {
      throw new ArchiveError('The archive’s central directory is truncated or corrupt.', {
        context: { entryIndex: i },
      });
    }
    const flags = view.getUint16(at + 8, true);
    const method = view.getUint16(at + 10, true);
    const dosTime = view.getUint16(at + 12, true);
    const dosDate = view.getUint16(at + 14, true);
    const expectedCrc = view.getUint32(at + 16, true);
    const compressedSize = view.getUint32(at + 20, true);
    const uncompressedSize = view.getUint32(at + 24, true);
    const nameLength = view.getUint16(at + 28, true);
    const extraLength = view.getUint16(at + 30, true);
    const commentLength = view.getUint16(at + 32, true);
    const localOffset = view.getUint32(at + 42, true);
    const name = decoder.decode(bytes.subarray(at + CENTRAL_HEADER_BYTES, at + CENTRAL_HEADER_BYTES + nameLength));

    if (method !== METHOD_STORE) {
      throw new ArchiveError(
        `"${name}" is compressed, and a Touchline archive is STORE-only (§11.7) — this file was not produced by this app.`,
        { context: { name, method } },
      );
    }
    if (compressedSize !== uncompressedSize) {
      throw new ArchiveError(`"${name}" declares different stored and original sizes.`, {
        context: { name, compressedSize, uncompressedSize },
      });
    }
    if (localOffset === MAX_UINT32 || uncompressedSize === MAX_UINT32) {
      throw new ArchiveError(`"${name}" uses zip64 extensions, which this reader does not support.`, {
        context: { name },
      });
    }

    if (localOffset + LOCAL_HEADER_BYTES > bytes.length || view.getUint32(localOffset, true) !== LOCAL_HEADER_SIGNATURE) {
      throw new ArchiveError(`"${name}" has no local file header at the offset the directory gives.`, {
        context: { name, localOffset },
      });
    }
    const localNameLength = view.getUint16(localOffset + 26, true);
    const localExtraLength = view.getUint16(localOffset + 28, true);
    const dataStart = localOffset + LOCAL_HEADER_BYTES + localNameLength + localExtraLength;
    if (dataStart + uncompressedSize > bytes.length) {
      throw new ArchiveError(`"${name}" runs past the end of the file.`, { context: { name } });
    }

    const data = bytes.slice(dataStart, dataStart + uncompressedSize);
    const actualCrc = crc32(data);
    if (actualCrc !== expectedCrc) {
      throw new ArchiveError(`"${name}" failed its CRC check; the archive is damaged.`, {
        context: { name, expectedCrc, actualCrc },
      });
    }

    records.push({ name, data, crc32: actualCrc, method, flags, dosTime, dosDate });
    at += CENTRAL_HEADER_BYTES + nameLength + extraLength + commentLength;
  }

  return records;
}

/** The fixed header values, exported so a determinism test asserts against the declaration. */
export const ZIP_INVARIANTS = {
  method: METHOD_STORE,
  flags: FLAG_UTF8,
  dosTime: FIXED_DOS_TIME,
  dosDate: FIXED_DOS_DATE,
} as const;
